import * as React from "react";
import { useState, useEffect } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, Droplets, Wind, Shield, Sparkles, Thermometer, Zap, Palette, Eye } from "lucide-react";
import { ProminentCTA } from "@/components/ui/prominent-cta";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { ProductGallery } from "@/components/ui/product-gallery";
import { SEO } from "@/components/SEO";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ui/contact-form";
import SocialShare from "@/components/blog/SocialShare";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";

// U1 Product Images
import u1ProductMain from "@/assets/u1-product-main.jpg";
import u1FeaturesGrid from "@/assets/u1-features-grid.jpg";
import u1ColorOptions from "@/assets/u1-color-options.jpg";
import u1DesignDark from "@/assets/u1-design-dark.jpg";
import u1AmbientLight from "@/assets/u1-ambient-light.jpg";
import u1WaterPressure from "@/assets/u1-water-pressure.jpg";
import u1BackflowTech from "@/assets/u1-backflow-tech.jpg";
import u1FlushSystem from "@/assets/u1-flush-system.jpg";

import { PriceFrom } from "@/components/products/PriceFrom";
export default function U1() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop();
  useOptimizedObserver();

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const productImages = [
    { src: u1ProductMain, alt: "U1 Smart Toilet - Overflow Series main product view with double anti-backflow technology", title: "Main View" },
    { src: u1FeaturesGrid, alt: "U1 Smart Toilet features including foam shield, UV sterilization, and automatic flip cover", title: "Smart Features" },
    { src: u1ColorOptions, alt: "U1 Smart Toilet sophisticated color options - Pure white, Gray screen, Black screen, Matte black", title: "Color Options" },
    { src: u1DesignDark, alt: "U1 Smart Toilet elegant design with flowing light and shadow lines", title: "Design Excellence" },
    { src: u1AmbientLight, alt: "U1 Smart Toilet ambient night light feature with misty blue illumination", title: "Ambient Lighting" },
    { src: u1WaterPressure, alt: "U1 Smart Toilet zero water pressure technology with independent tank system", title: "Zero Pressure Flush" },
    { src: u1BackflowTech, alt: "U1 Smart Toilet double backflow prevention with siphon and jet assist", title: "Backflow Prevention" },
    { src: u1FlushSystem, alt: "U1 Smart Toilet dual-level flushing system with vortex and jet boost", title: "Dual Flush System" },
  ];

  const premiumFeatures = [
    {
      icon: <Shield className="w-8 h-8 text-cyan-500" />,
      title: "Double Anti-Backflow Technology",
      description: "Dual waterway flushing with siphon scouring and jet assist prevents waste backflow, eliminates odor return, and reduces water usage"
    },
    {
      icon: <Zap className="w-8 h-8 text-amber-400" />,
      title: "Zero Water Pressure Use",
      description: "Independent water tank with powerful pressurization - works perfectly in high-rise buildings with low or unstable water pressure"
    },
    {
      icon: <Droplets className="w-8 h-8 text-blue-400" />,
      title: "UV Water Sterilization",
      description: "Built-in UV purification system sterilizes every drop of water from the source for ultimate hygiene"
    },
    {
      icon: <Wind className="w-8 h-8 text-orange-400" />,
      title: "Foam Shield Technology",
      description: "Built-in foam bottle creates multi-effect protective barrier - hidden design saves space while providing superior coverage"
    },
    {
      icon: <Eye className="w-8 h-8 text-indigo-400" />,
      title: "Ambient Night Light",
      description: "Misty blue diffused lighting for nighttime use - silent guardian that ensures calm bathroom experience without harsh lights"
    },
    {
      icon: <Palette className="w-8 h-8 text-purple-400" />,
      title: "Sophisticated Color Options",
      description: "Available in Pure White, Gray Screen, Black Screen, and exclusive Matte Black - break away from conventional designs"
    }
  ];

  const keyFeatures = [
    "Automatic flip cover with proximity sensor",
    "Ergonomic heated seat with memory function",
    "360° three-dimensional vortex flush",
    "Jet boost powerful surge technology",
    "Large capacity hidden water tank",
    "High-power booster pump",
    "Warm air drying system",
    "Clean washing for the whole family",
    "Soft-close mechanism",
    "Foot sensor for hands-free flushing",
    "Triple massage bidet function",
    "Built-in bubbles technology"
  ];

  const specifications = [
    { label: "Series", value: "Overflow Series" },
    { label: "Model", value: "U1 Smart Toilet" },
    { label: "Flush System", value: "Dual-level (Siphon + Jet Boost)" },
    { label: "Water Tank", value: "Large capacity hidden tank" },
    { label: "Pump", value: "High-power booster pump" },
    { label: "Color Options", value: "White, Gray, Black, Matte Black" },
    { label: "Controls", value: "Touch panel + Remote" },
    { label: "Safety", value: "Anti-backflow, UV sterilization" }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "U1 Smart Toilet - Overflow Series",
    "description": "The U1 Overflow Series smart toilet features revolutionary double anti-backflow technology, zero water pressure operation, UV sterilization, foam shield, and ambient lighting. Premium construction with multiple sophisticated color options.",
    "brand": {
      "@type": "Brand",
      "name": "Aureum VII Trading"
    },
    "image": [
      "https://www.aureum7trading.com/images/products/u1-product-main.jpg"
    ],
    "gtin13": "0795479810017",
    "manufacturer": {
      "@type": "Organization",
      "name": "Aureum VII Trading"
    },
    "sku": "U1-OVERFLOW",
    "mpn": "U1",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "price": 600,
      "priceValidUntil": "2026-12-31",
      "url": "https://www.aureum7trading.com/products/u1",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Aureum VII Trading",
        "url": "https://www.aureum7trading.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 10,
            "maxValue": 40,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "@id": "https://www.aureum7trading.com/return-policy",
        "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn",
        "itemCondition": "https://schema.org/NewCondition",
        "refundType": "https://schema.org/FullRefund"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 text-foreground">
      <SEO
        title="U1 Overflow Smart Toilet | Double Anti-Backflow Tech"
        description="U1 Overflow smart toilet with double anti-backflow protection, zero-pressure operation, UV sterilization and foam shield — built for high-rise projects."
        keywords="U1 smart toilet, Overflow Series, anti-backflow toilet, zero water pressure toilet, UV sterilization bidet, foam shield toilet, ambient light toilet, luxury smart toilet Europe, high-rise building toilet, sophisticated toilet colors"
        url="/products/u1"
        image="https://www.aureum7trading.com/images/products/u1-product-main.jpg"
        structuredData={productStructuredData}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "U1 Overflow Series", url: "/products/u1" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 lg:px-12 container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-in-observer">
            <Badge variant="outline" className="border-cyan-300/50 text-cyan-600 bg-cyan-50/80 mb-4 font-medium">
              Overflow Series
            </Badge>
            <h1 className="font-playfair text-5xl md:text-7xl font-light mb-6 bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent tracking-tight">
              U1
              <span className="block text-2xl md:text-3xl text-primary font-medium mt-1">
                Smart Toilet
              </span>
            </h1>
            <PriceFrom price={600} />
            <p className="font-inter text-xl text-muted-foreground leading-relaxed mb-8">
              The U1 Overflow Series represents a new era of bathroom intelligence. Featuring revolutionary 
              <strong> double anti-backflow technology</strong>, this premium <LocalizedLink to="/smart-toilets" className="text-primary hover:underline font-semibold">smart toilet</LocalizedLink> 
              operates flawlessly even with zero water pressure - perfect for high-rise buildings. UV sterilization, 
              foam shield protection, and ambient night lighting create an unparalleled bathroom experience. 
              Available in sophisticated color options that break away from conventional designs.
            </p>

            
            {/* Key Highlights */}
            <div className="mb-8 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className="font-inter text-muted-foreground">Double anti-backflow net force technology</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className="font-inter text-muted-foreground">Zero water pressure operation</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className="font-inter text-muted-foreground">UV water sterilization system</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className="font-inter text-muted-foreground">Multiple sophisticated color options</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                <span className="font-inter text-muted-foreground">Ambient night light system</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Button asChild className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                <a href="#contact-section">Request Quote</a>
              </Button>
              <Button variant="outline" asChild>
                <LocalizedLink to="/smart-toilets">← Back to Collections</LocalizedLink>
              </Button>
            </div>
          </div>

          <div className="fade-in-observer">
            <img 
              src={u1ProductMain} 
              alt="U1 Overflow Series smart toilet featuring double anti-backflow technology, elegant white ceramic design with blue accent lighting, premium construction for luxury bathroom spaces"
              className="w-full h-auto rounded-2xl shadow-luxury"
              width="600"
              height="800"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Complete Product Gallery</h2>
        <p className="font-inter text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          Explore every detail of the U1 Overflow Series - from innovative technology to sophisticated design options.
        </p>
        <ProductGallery images={productImages} className="max-w-4xl mx-auto" />
      </section>

      {/* Premium Features */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-4 text-center">Revolutionary Smart Features</h2>
        <p className="font-inter text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          The U1 Overflow Series combines cutting-edge technology with premium craftsmanship for the ultimate bathroom experience.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {premiumFeatures.map((feature, index) => (
            <FeatureCardSimple
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </section>

      {/* Technical Details Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-slate-900 to-slate-800 text-white fade-in-observer">
        <div className="container mx-auto max-w-7xl">
          <h2 className="font-playfair text-3xl font-medium text-white mb-4 text-center">Dual Flush Technology</h2>
          <p className="font-inter text-slate-300 text-center mb-12 max-w-3xl mx-auto">
            The U1 features a revolutionary two-level flushing system combining vortex siphon technology with jet boost power for unlimited, efficient flushing.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <Badge className="bg-amber-500 text-white mb-4">Level 1</Badge>
              <h3 className="font-playfair text-2xl font-medium text-white mb-4">Siphon Scour - Vortex Flush</h3>
              <p className="text-slate-300">
                360° three-dimensional flushing with vortex centrifugal water pressure sweeping away dirt effectively. Creates a powerful cleaning action from all angles.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
              <Badge className="bg-cyan-500 text-white mb-4">Level 2</Badge>
              <h3 className="font-playfair text-2xl font-medium text-white mb-4">Jet Boost - Powerful Surge</h3>
              <p className="text-slate-300">
                Aims at the center of the sewage outlet and accelerates to flush out the dirt. Combined with the high-power booster pump for complete waste removal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Complete Feature List */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Complete Smart Experience</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {keyFeatures.map((feature, index) => (
            <div key={index} className="shadow-soft bg-white rounded-lg p-6 text-center">
              <Sparkles className="w-8 h-8 text-cyan-500 mx-auto mb-3" />
              <p className="font-inter text-muted-foreground">{feature}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 px-4 sm:px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-7xl fade-in-observer">
        <h2 className="font-playfair text-3xl font-medium text-primary mb-8 text-center">Technical Specifications</h2>
        <div className="max-w-2xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-4">
            {specifications.map((spec, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border-b border-cyan-200/50">
                <span className="text-sm text-muted-foreground block">{spec.label}</span>
                <span className="font-medium text-primary">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shipping Info */}
      <ShippingTimeInfo />

      {/* Contact Section */}
      <section id="contact-section" className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-6">Request a Quote</h2>
            <p className="font-inter text-muted-foreground mb-8">
              Interested in the U1 Overflow Series for your luxury bathroom project? Contact our team for 
              personalized pricing, bulk orders, and installation consultation.
            </p>
            <div className="space-y-4 text-muted-foreground">
              <p>✓ Expert consultation available</p>
              <p>✓ Bulk order discounts</p>
              <p>✓ Professional installation support</p>
              <p>✓ 5-year comprehensive warranty</p>
            </div>
          </div>
          <div className="bg-white p-8 rounded-xl shadow-soft">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Social Share */}
      <section className="py-8 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-muted/30 rounded-xl">
          <span className="font-inter text-muted-foreground">Share this product:</span>
          <SocialShare 
            url="https://www.aureum7trading.com/products/u1" 
            title="U1 Smart Toilet - Overflow Series | Aureum VII Trading" 
          />
        </div>
      </section>

      {/* CTA Section */}
      <ProminentCTA 
        title="Ready to Transform Your Bathroom?"
        description="Contact our team for personalized consultation and pricing."
        variant="gradient"
      />

      {/* Related Products */}
      <RelatedProducts currentProduct="U1" />

      <Footer />

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 right-6 z-40 p-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all duration-300"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
